import { PUBLIC_HOST } from "../lib/domain";
/**
 * Espace Coach — Cockpit admin (/app/coachs)
 * Gère les fiches coachs (Phase 1), génère les liens "Mini Espace Coach",
 * et affiche les impressions déposées par les coachs.
 */
import { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import {
  Users, Plus, Pencil, Trash2, Link as LinkIcon, Copy, MessageSquare,
  Loader2, X, Eye, EyeOff, Award, GraduationCap, RefreshCw, Send, Mail,
} from "lucide-react";

const ROLE_BADGE = {
  1: { label: "Coach", color: "bg-amber-500/20 text-amber-300 border-amber-500/40" },
  2: { label: "Assistant / Senpai", color: "bg-sky-500/20 text-sky-300 border-sky-500/40" },
  3: { label: "Directeur technique", color: "bg-red-500/20 text-red-300 border-red-500/40" },
};

const EMPTY = {
  full_name: "", display_name: "", role_level: 1, title: "", disciplines: [],
  internal_rank: "", years_experience: "", phone: "", email: "", assigned_groups: [], pedagogical_strengths: "",
  areas_to_improve: "", monthly_kaizen_goal: "", role_in_school: "", bio_short: "",
  philosophy: "", photo_url: "", public_visible: true, active: true,
};

const CARNET_DISCIPLINES = [
  { v: "jujutsu_japonais", label: "Jiu-jitsu Can Ryu" },
  { v: "bjj", label: "Jiu-Jitsu Brésilien (BJJ)" },
  { v: "muay_thai", label: "Muay-Thaï" },
  { v: "kickboxing", label: "Kickboxing" },
];

const EMPTY_CARNET = {
  full_name: "", display_name: "", title: "", internal_rank: "Ceinture noire",
  phone: "", email: "", disciplines: [],
  carnet_discipline: "jujutsu_japonais", carnet_age: 30,
  carnet_current_belt: "Ceinture noire", carnet_target_belt: "Perfectionnement continu",
  channels: ["sms", "email"], public_visible: true, active: true,
};

export default function CoachCockpitModule() {
  const [coaches, setCoaches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [impressionsFor, setImpressionsFor] = useState(null);
  const [impressions, setImpressions] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/coach-cockpit/coaches");
      setCoaches(r.data.coaches || []);
    } catch (e) {
      toast.error("Erreur chargement coachs");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const seed = async () => {
    try {
      await api.post("/coach-cockpit/seed");
      toast.success("Équipe initialisée (Sonia, Karl, Sandra, Nancy)");
      load();
    } catch (e) { toast.error("Erreur seed"); }
  };

  const save = async () => {
    const payload = { ...editing };
    if (payload.years_experience === "") payload.years_experience = null;
    else if (payload.years_experience != null) payload.years_experience = parseInt(payload.years_experience, 10);
    try {
      if (editing.id) {
        await api.patch(`/coach-cockpit/coaches/${editing.id}`, payload);
        toast.success("Fiche mise à jour");
      } else {
        await api.post("/coach-cockpit/coaches", payload);
        toast.success("Coach ajouté");
      }
      setEditing(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur sauvegarde"); }
  };

  const remove = async (c) => {
    if (!window.confirm(`Supprimer ${c.full_name} ?`)) return;
    try {
      await api.delete(`/coach-cockpit/coaches/${c.id}`);
      toast.success("Coach supprimé");
      load();
    } catch (e) { toast.error("Erreur suppression"); }
  };

  const copyLink = async (c) => {
    try {
      const r = await api.post(`/coach-cockpit/coaches/${c.id}/space-link`);
      const url = `${window.location.origin}${r.data.space_path}`;
      navigator.clipboard.writeText(url);
      toast.success(`Lien Espace Coach de ${c.display_name} copié !`);
    } catch (e) { toast.error("Erreur génération lien"); }
  };

  const viewImpressions = async (c) => {
    setImpressionsFor(c);
    try {
      const r = await api.get(`/coach-cockpit/coaches/${c.id}/impressions`);
      setImpressions(r.data.impressions || []);
    } catch (e) { toast.error("Erreur impressions"); }
  };

  const [inviting, setInviting] = useState(null);

  const toggleSms = async (c) => {
    const next = c.sms_enabled === false; // si désactivé -> on réactive
    try {
      await api.patch(`/coach-cockpit/coaches/${c.id}`, { sms_enabled: next });
      toast.success(`SMS ${next ? "activés" : "désactivés"} pour ${c.display_name}`);
      load();
    } catch (e) { toast.error("Erreur mise à jour SMS"); }
  };

  const invite = async (c) => {
    if (!window.confirm(`Envoyer l'invitation (SMS + courriel) à ${c.full_name} ?\n\n⚠️ Les liens pointent vers ${PUBLIC_HOST} — assure-toi d'avoir redéployé en production.`)) return;
    setInviting(c.id);
    try {
      const r = await api.post(`/coach-cockpit/coaches/${c.id}/invite`, { channels: ["sms", "email"] });
      const ok = (r.data.results || []).filter((x) => x.sent).map((x) => x.channel);
      const fail = (r.data.results || []).filter((x) => !x.sent && x.reason && x.reason !== "no_phone" && x.reason !== "no_email");
      if (ok.length) toast.success(`Invitation envoyée à ${c.display_name} (${ok.join(" + ")})`);
      // Si au moins un canal est parti, l'échec de l'autre canal est un simple avertissement (pas une erreur bloquante)
      if (fail.length) {
        const msg = `SMS non envoyé pour ${c.display_name} : ${fail.map((x) => x.reason).join(" · ")}`;
        if (ok.length) toast.message(msg);
        else toast.error(msg);
      }
      if (!ok.length && !fail.length) toast.error("Aucun canal disponible (pas de téléphone ni de courriel valide)");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur d'invitation"); }
    finally { setInviting(null); }
  };

  const inviteAll = async () => {
    if (!window.confirm("Envoyer l'invitation (SMS + courriel) à TOUS les coachs ?\n\n⚠️ Assure-toi d'avoir redéployé en production (sinon les liens donneront un 404).")) return;
    setInviting("all");
    try {
      const r = await api.post(`/coach-cockpit/invite-all`, { channels: ["sms", "email"] });
      toast.success(`${r.data.invited} coach(s) invité(s)`);
      if ((r.data.skipped || []).length) toast.message(`${r.data.skipped.length} ignoré(s) (pas de contact)`);
      load();
    } catch (e) { toast.error("Erreur invitation groupée"); }
    finally { setInviting(null); }
  };

  const [carnetInvite, setCarnetInvite] = useState(null);
  const [carnetBusy, setCarnetBusy] = useState(false);
  const setCarnetField = (k, v) => setCarnetInvite((p) => ({ ...p, [k]: v }));

  const submitCarnetInvite = async () => {
    if (!carnetInvite.full_name?.trim()) { toast.error("Le nom est requis"); return; }
    if (!carnetInvite.phone && !carnetInvite.email) { toast.error("Ajoute un téléphone OU un courriel"); return; }
    if (!window.confirm(`Créer le profil coach de ${carnetInvite.full_name}, générer son carnet et lui envoyer les 2 liens (${(carnetInvite.channels || []).join(" + ") || "aucun canal"}) ?\n\n⚠️ Les liens pointent vers ${PUBLIC_HOST} — assure-toi d'avoir redéployé en production.`)) return;
    setCarnetBusy(true);
    try {
      const payload = {
        ...carnetInvite,
        carnet_age: parseInt(carnetInvite.carnet_age, 10) || 30,
        disciplines: Array.isArray(carnetInvite.disciplines) ? carnetInvite.disciplines : [],
      };
      const r = await api.post(`/coach-cockpit/coaches/invite-with-carnet`, payload);
      const ok = (r.data.results || []).filter((x) => x.sent).map((x) => x.channel);
      const fail = (r.data.results || []).filter((x) => !x.sent);
      if (ok.length) toast.success(`Coach créé + carnet généré · liens envoyés (${ok.join(" + ")})`);
      else toast.success("Coach créé + carnet généré (aucun envoi : vérifie contact/connexions)");
      if (fail.length) toast.message(`Non envoyé : ${fail.map((x) => `${x.channel} (${x.reason})`).join(", ")}`);
      setCarnetInvite(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur invitation + carnet"); }
    finally { setCarnetBusy(false); }
  };

  const setField = (k, v) => setEditing((p) => ({ ...p, [k]: v }));
  const setList = (k, v) => setField(k, v.split(",").map((s) => s.trim()).filter(Boolean));

  return (
    <div className="p-6 max-w-6xl mx-auto" data-testid="coach-cockpit-module">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold text-white flex items-center gap-2">
            <Users className="text-amber-400" /> Espace Coach
          </h1>
          <p className="text-zinc-400 text-sm mt-1">
            Fiches coachs · liens privés "Mini Espace Coach" · impressions de l'équipe.
          </p>
        </div>
        <div className="flex gap-2">
          <a href="/equipe" target="_blank" rel="noreferrer"
            className="px-3 py-2 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-zinc-200 text-sm flex items-center gap-2"
            data-testid="view-public-team-btn">
            <Eye size={16} /> Vitrine publique
          </a>
          <button onClick={seed} className="px-3 py-2 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-zinc-200 text-sm flex items-center gap-2" data-testid="seed-coaches-btn">
            <RefreshCw size={16} /> Initialiser l'équipe
          </button>
          <button onClick={inviteAll} disabled={inviting === "all"}
            className="px-3 py-2 rounded-lg bg-emerald-700 hover:bg-emerald-600 text-white text-sm flex items-center gap-2 disabled:opacity-50"
            data-testid="invite-all-btn">
            {inviting === "all" ? <Loader2 className="animate-spin" size={16} /> : <Send size={16} />} Inviter tous
          </button>
          <button onClick={() => setCarnetInvite({ ...EMPTY_CARNET })}
            className="px-3 py-2 rounded-lg bg-gradient-to-r from-amber-600 to-red-600 hover:from-amber-500 hover:to-red-500 text-white text-sm flex items-center gap-2"
            data-testid="invite-carnet-btn">
            <GraduationCap size={16} /> Inviter + carnet
          </button>
          <button onClick={() => setEditing({ ...EMPTY })}
            className="px-3 py-2 rounded-lg bg-amber-600 hover:bg-amber-500 text-white text-sm flex items-center gap-2"
            data-testid="add-coach-btn">
            <Plus size={16} /> Ajouter
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="animate-spin text-amber-400" /></div>
      ) : coaches.length === 0 ? (
        <div className="text-center py-20 text-zinc-500">
          <Users size={48} className="mx-auto mb-3 opacity-40" />
          <p>Aucun coach. Clique "Initialiser l'équipe" pour démarrer.</p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {coaches.map((c) => {
            const rb = ROLE_BADGE[c.role_level] || ROLE_BADGE[1];
            return (
              <div key={c.id} className="bg-zinc-900 border border-zinc-800 rounded-xl p-5" data-testid={`coach-card-${c.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-amber-500 to-red-600 flex items-center justify-center text-white font-bold text-lg shrink-0">
                      {(c.display_name || c.full_name || "?")[0]}
                    </div>
                    <div className="min-w-0">
                      <div className="font-bold text-white truncate">{c.full_name}</div>
                      <div className="text-xs text-zinc-400 truncate">{c.title || "—"}</div>
                    </div>
                  </div>
                  <span className={`text-[10px] px-2 py-1 rounded-full border ${rb.color} whitespace-nowrap`}>{rb.label}</span>
                </div>

                <div className="mt-3 flex flex-wrap gap-1">
                  {(c.disciplines || []).map((d) => (
                    <span key={d} className="text-[10px] px-2 py-0.5 rounded bg-zinc-800 text-zinc-300">{d.replace(/_/g, " ")}</span>
                  ))}
                  {c.internal_rank && <span className="text-[10px] px-2 py-0.5 rounded bg-amber-900/40 text-amber-300 flex items-center gap-1"><Award size={10} />{c.internal_rank}</span>}
                  {!c.public_visible && <span className="text-[10px] px-2 py-0.5 rounded bg-zinc-800 text-zinc-500 flex items-center gap-1"><EyeOff size={10} />privé</span>}
                  {c.invited_at && <span className="text-[10px] px-2 py-0.5 rounded bg-emerald-900/40 text-emerald-300 flex items-center gap-1"><Send size={10} />invité</span>}
                  {!(c.phone || c.email) && <span className="text-[10px] px-2 py-0.5 rounded bg-red-900/40 text-red-300 flex items-center gap-1"><Mail size={10} />sans contact</span>}
                </div>

                {(c.assigned_groups || []).length > 0 && (
                  <div className="mt-3 text-xs text-zinc-400 flex items-center gap-1">
                    <GraduationCap size={13} className="text-amber-400" />
                    <span className="truncate">{c.assigned_groups.join(" · ")}</span>
                  </div>
                )}
                {c.monthly_kaizen_goal && (
                  <div className="mt-2 text-xs text-amber-200/80 bg-amber-950/30 rounded px-2 py-1">🎯 {c.monthly_kaizen_goal}</div>
                )}

                <div className="mt-4 flex flex-wrap gap-2">
                  <button onClick={() => invite(c)} disabled={inviting === c.id}
                    className="text-xs px-2.5 py-1.5 rounded bg-amber-700/80 hover:bg-amber-600 text-white flex items-center gap-1 disabled:opacity-50"
                    data-testid={`coach-invite-${c.id}`}>
                    {inviting === c.id ? <Loader2 className="animate-spin" size={13} /> : <Send size={13} />} Inviter
                  </button>
                  <button onClick={() => copyLink(c)} className="text-xs px-2.5 py-1.5 rounded bg-emerald-700/80 hover:bg-emerald-600 text-white flex items-center gap-1" data-testid={`coach-link-${c.id}`}>
                    <LinkIcon size={13} /> Lien Espace
                  </button>
                  <button onClick={() => toggleSms(c)}
                    className={`text-xs px-2.5 py-1.5 rounded flex items-center gap-1 ${c.sms_enabled === false ? "bg-zinc-800 text-zinc-500 hover:bg-zinc-700" : "bg-sky-700/80 hover:bg-sky-600 text-white"}`}
                    data-testid={`coach-sms-toggle-${c.id}`}
                    title={c.sms_enabled === false ? "SMS désactivés — cliquer pour réactiver" : "SMS activés — cliquer pour désactiver (envoi courriel seulement)"}>
                    {c.sms_enabled === false ? "🔕 SMS off" : "🔔 SMS on"}
                  </button>
                  <button onClick={() => viewImpressions(c)} className="text-xs px-2.5 py-1.5 rounded bg-zinc-800 hover:bg-zinc-700 text-zinc-200 flex items-center gap-1" data-testid={`coach-impressions-${c.id}`}>
                    <MessageSquare size={13} /> Impressions ({c.impressions_count})
                  </button>
                  <button onClick={() => setEditing({ ...EMPTY, ...c })} className="text-xs px-2.5 py-1.5 rounded bg-zinc-800 hover:bg-zinc-700 text-zinc-200 flex items-center gap-1">
                    <Pencil size={13} /> Éditer
                  </button>
                  <button onClick={() => remove(c)} className="text-xs px-2.5 py-1.5 rounded bg-zinc-800 hover:bg-red-900/60 text-red-300 flex items-center gap-1">
                    <Trash2 size={13} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Edit dialog */}
      {editing && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={() => setEditing(null)}>
          <div className="bg-zinc-900 border border-zinc-700 rounded-xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="coach-edit-dialog">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-white">{editing.id ? "Éditer la fiche" : "Nouveau coach"}</h2>
              <button onClick={() => setEditing(null)} className="text-zinc-400 hover:text-white"><X /></button>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <Field label="Nom légal *"><input className="inp" value={editing.full_name} onChange={(e) => setField("full_name", e.target.value)} data-testid="coach-fullname-input" /></Field>
              <Field label="Nom d'affichage"><input className="inp" value={editing.display_name} onChange={(e) => setField("display_name", e.target.value)} /></Field>
              <Field label="Niveau / Rôle">
                <select className="inp" value={editing.role_level} onChange={(e) => setField("role_level", parseInt(e.target.value, 10))}>
                  <option value={1}>1 · Instructeur / Coach</option>
                  <option value={2}>2 · Assistant / Senpai</option>
                  <option value={3}>3 · Directeur technique</option>
                </select>
              </Field>
              <Field label="Titre exact"><input className="inp" value={editing.title || ""} onChange={(e) => setField("title", e.target.value)} /></Field>
              <Field label="Disciplines (virgules)"><input className="inp" value={(editing.disciplines || []).join(", ")} onChange={(e) => setList("disciplines", e.target.value)} /></Field>
              <Field label="Rang / certification interne"><input className="inp" value={editing.internal_rank || ""} onChange={(e) => setField("internal_rank", e.target.value)} /></Field>
              <Field label="Années d'expérience"><input className="inp" type="number" value={editing.years_experience ?? ""} onChange={(e) => setField("years_experience", e.target.value)} /></Field>
              <Field label="Téléphone (SMS)"><input className="inp" value={editing.phone || ""} onChange={(e) => setField("phone", e.target.value)} placeholder="+1 819..." data-testid="coach-phone-input" /></Field>
              <Field label="Courriel"><input className="inp" value={editing.email || ""} onChange={(e) => setField("email", e.target.value)} placeholder="coach@email.com" data-testid="coach-email-input" /></Field>
              <Field label="Groupes assignés (virgules)"><input className="inp" value={(editing.assigned_groups || []).join(", ")} onChange={(e) => setList("assigned_groups", e.target.value)} /></Field>
              <Field label="Forces pédagogiques" full><textarea className="inp" rows={2} value={editing.pedagogical_strengths || ""} onChange={(e) => setField("pedagogical_strengths", e.target.value)} /></Field>
              <Field label="Points à travailler" full><textarea className="inp" rows={2} value={editing.areas_to_improve || ""} onChange={(e) => setField("areas_to_improve", e.target.value)} /></Field>
              <Field label="Objectif Kaizen mensuel" full><input className="inp" value={editing.monthly_kaizen_goal || ""} onChange={(e) => setField("monthly_kaizen_goal", e.target.value)} /></Field>
              <Field label="Rôle dans l'école"><input className="inp" value={editing.role_in_school || ""} onChange={(e) => setField("role_in_school", e.target.value)} /></Field>
              <Field label="Photo (URL)"><input className="inp" value={editing.photo_url || ""} onChange={(e) => setField("photo_url", e.target.value)} /></Field>
              <Field label="Bio courte (parents)" full><textarea className="inp" rows={2} value={editing.bio_short || ""} onChange={(e) => setField("bio_short", e.target.value)} /></Field>
              <Field label="Philosophie / citation" full><input className="inp" value={editing.philosophy || ""} onChange={(e) => setField("philosophy", e.target.value)} /></Field>
            </div>
            <label className="flex items-center gap-2 mt-3 text-sm text-zinc-300">
              <input type="checkbox" checked={!!editing.public_visible} onChange={(e) => setField("public_visible", e.target.checked)} />
              Visible sur la vitrine publique (parents)
            </label>
            <div className="flex justify-end gap-2 mt-5">
              <button onClick={() => setEditing(null)} className="px-4 py-2 rounded bg-zinc-800 hover:bg-zinc-700 text-zinc-200 text-sm">Annuler</button>
              <button onClick={save} className="px-4 py-2 rounded bg-amber-600 hover:bg-amber-500 text-white text-sm" data-testid="coach-save-btn">Enregistrer</button>
            </div>
          </div>
        </div>
      )}

      {/* Inviter + carnet dialog */}
      {carnetInvite && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={() => setCarnetInvite(null)}>
          <div className="bg-zinc-900 border border-amber-700/40 rounded-xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="invite-carnet-dialog">
            <div className="flex items-center justify-between mb-1">
              <h2 className="text-xl font-bold text-white flex items-center gap-2"><GraduationCap className="text-amber-400" size={20} /> Inviter comme coach + envoyer son carnet</h2>
              <button onClick={() => setCarnetInvite(null)} className="text-zinc-400 hover:text-white"><X /></button>
            </div>
            <p className="text-xs text-zinc-400 mb-4">En 1 clic : crée la fiche coach, génère son carnet de progression, et envoie les 2 liens (Espace Coach + Carnet) par SMS et/ou courriel.</p>
            <div className="grid sm:grid-cols-2 gap-3">
              <Field label="Nom légal *"><input className="inp" value={carnetInvite.full_name} onChange={(e) => setCarnetField("full_name", e.target.value)} placeholder="Eliane Massé" data-testid="carnet-fullname-input" /></Field>
              <Field label="Titre / rôle"><input className="inp" value={carnetInvite.title} onChange={(e) => setCarnetField("title", e.target.value)} placeholder="Instructrice Can-Ryu" /></Field>
              <Field label="Téléphone (SMS)"><input className="inp" value={carnetInvite.phone} onChange={(e) => setCarnetField("phone", e.target.value)} placeholder="+1 819..." data-testid="carnet-phone-input" /></Field>
              <Field label="Courriel"><input className="inp" value={carnetInvite.email} onChange={(e) => setCarnetField("email", e.target.value)} placeholder="coach@email.com" data-testid="carnet-email-input" /></Field>
            </div>
            <div className="mt-4 pt-3 border-t border-zinc-800">
              <div className="text-xs font-semibold text-amber-300 mb-2">📓 Carnet de progression</div>
              <div className="grid sm:grid-cols-2 gap-3">
                <Field label="Discipline du carnet">
                  <select className="inp" value={carnetInvite.carnet_discipline} onChange={(e) => setCarnetField("carnet_discipline", e.target.value)} data-testid="carnet-discipline-select">
                    {CARNET_DISCIPLINES.map((d) => <option key={d.v} value={d.v}>{d.label}</option>)}
                  </select>
                </Field>
                <Field label="Âge"><input className="inp" type="number" value={carnetInvite.carnet_age} onChange={(e) => setCarnetField("carnet_age", e.target.value)} /></Field>
                <Field label="Ceinture actuelle"><input className="inp" value={carnetInvite.carnet_current_belt} onChange={(e) => setCarnetField("carnet_current_belt", e.target.value)} data-testid="carnet-belt-input" /></Field>
                <Field label="Objectif / ceinture cible"><input className="inp" value={carnetInvite.carnet_target_belt} onChange={(e) => setCarnetField("carnet_target_belt", e.target.value)} /></Field>
              </div>
            </div>
            <div className="mt-4 flex items-center gap-4">
              <span className="text-xs text-zinc-400">Envoyer par :</span>
              {["sms", "email"].map((ch) => (
                <label key={ch} className="flex items-center gap-1.5 text-sm text-zinc-200">
                  <input type="checkbox" checked={(carnetInvite.channels || []).includes(ch)}
                    onChange={(e) => {
                      const set = new Set(carnetInvite.channels || []);
                      e.target.checked ? set.add(ch) : set.delete(ch);
                      setCarnetField("channels", Array.from(set));
                    }} data-testid={`carnet-channel-${ch}`} />
                  {ch === "sms" ? "SMS" : "Courriel"}
                </label>
              ))}
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <button onClick={() => setCarnetInvite(null)} className="px-4 py-2 rounded bg-zinc-800 hover:bg-zinc-700 text-zinc-200 text-sm">Annuler</button>
              <button onClick={submitCarnetInvite} disabled={carnetBusy}
                className="px-4 py-2 rounded bg-gradient-to-r from-amber-600 to-red-600 hover:from-amber-500 hover:to-red-500 text-white text-sm flex items-center gap-2 disabled:opacity-50"
                data-testid="carnet-submit-btn">
                {carnetBusy ? <Loader2 className="animate-spin" size={15} /> : <Send size={15} />} Créer + envoyer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Impressions dialog */}
      {impressionsFor && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={() => setImpressionsFor(null)}>
          <div className="bg-zinc-900 border border-zinc-700 rounded-xl p-6 max-w-lg w-full max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="impressions-dialog">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-white">Impressions · {impressionsFor.full_name}</h2>
              <button onClick={() => setImpressionsFor(null)} className="text-zinc-400 hover:text-white"><X /></button>
            </div>
            {impressions.length === 0 ? (
              <p className="text-zinc-500 text-sm text-center py-8">Aucune impression déposée pour l'instant.</p>
            ) : (
              <div className="space-y-3">
                {impressions.map((im) => (
                  <div key={im.id} className="bg-zinc-800/60 rounded-lg p-3">
                    <div className="flex items-center justify-between text-xs text-zinc-400 mb-1">
                      <span className="px-2 py-0.5 rounded bg-zinc-700 text-zinc-300">{im.category}</span>
                      {im.rating && <span className="text-amber-300">★ {im.rating}/10</span>}
                    </div>
                    <p className="text-sm text-zinc-100 whitespace-pre-wrap">{im.message}</p>
                    <div className="text-[10px] text-zinc-500 mt-1">{new Date(im.created_at).toLocaleString("fr-CA")}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      <style>{`.inp{width:100%;background:#18181b;border:1px solid #3f3f46;border-radius:8px;padding:8px 10px;color:#fff;font-size:13px;outline:none}.inp:focus{border-color:#d97706}`}</style>
    </div>
  );
}

function Field({ label, children, full }) {
  return (
    <div className={full ? "sm:col-span-2" : ""}>
      <label className="block text-xs text-zinc-400 mb-1">{label}</label>
      {children}
    </div>
  );
}
